import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps } from 'formik';
import { Col, Row } from 'antd/lib/grid';

import { Nullable } from '@common/typescript/objects/Nullable';

import { PostalCodeAutocomplete } from '@app/components/UI/Inputs/Address/PostalCodeAutocomplete';
import { PostalCode } from '@app/objects/PostalCode';
import { RegionSelect } from '@app/components/UI/Inputs/Address/RegionSelect';
import { iKey } from '@app/components/Pages/PetEditor/Services';
import { Card, getCardFields } from '@app/components/UI/Cards/Card';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';
import { BaseSectionProps, getDeliveryInfoFields } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/ViewContent';
import { addressService } from '@app/services/AddressService';
import { FormikField } from '@app/components/Forms/FormikField';
import { Clinic } from '@app/objects/Clinic';

type ColumnProps = Pick<React.PropsWithChildren<unknown>, 'children'>;
const Column: React.FC<ColumnProps> = (props: ColumnProps) => <Col xs={24} xl={12}>{props.children}</Col>;
interface DeliverySectionProps extends BaseSectionProps {
	clinic: Nullable<Clinic>;
	required: boolean;
}

export const DeliveryDetails: React.FC<Partial<DeliverySectionProps>> = ({ mode, clinic, required }: Partial<DeliverySectionProps>) => {
	const { t } = useTranslation();
	const country = clinic?.country?.alpha3 ?? 'unknown';
	const details = addressService.provide(country);

	return (
		<Card
			name="Delivery Details"
			mode={mode}
			fullWidth

			editContent={(
				<Row gutter={[30, 16]}>
					<Column>
						<FormikField fieldName="deliveryAddress" title={`${t(iKey('labels.delivery-address-1'))}`} required={required} />
					</Column>
					<Column>
						<FormikField fieldName="deliveryAddress2" title={t(iKey('labels.delivery-address-2'))} />
					</Column>
					<Column>
						<FormikField
							fieldName="deliveryZip"
							title={`${t(iKey(`labels.delivery-${details.mailKey}`))}`}
							required={required}
							render={(fieldProps: FieldProps<string, FormValues>) => (
								<PostalCodeAutocomplete
									value={fieldProps.field.value}
									onChange={(value: string) =>
										fieldProps.form.setFieldValue(fieldProps.field.name as keyof FormValues, value, false)}
									onSelect={(value, item?: PostalCode) => {
										if (!item) return;

										fieldProps.form.setValues({
											...fieldProps.form.values,
											deliveryZip: item.zip,
											deliveryCity: item.city,
											deliveryStateId: item.stateId,
										});
									}}
									country={country}
								/>
							)}
						/>
					</Column>
					<Column>
						<FormikField
							fieldName="deliveryCity"
							title={`${t(iKey('labels.delivery-city'))}`}
							required={required}
						/>
					</Column>
					<Column>
						<FormikField
							fieldName="deliveryStateId"
							title={`${t(iKey(`labels.delivery-${details.regionKey}`))}`}
							required={required}
							render={({ field, form }) => (
								<RegionSelect
									value={field.value}
									onChange={(state?: number) => {
										form.setFieldValue(field.name, state);
										form.setFieldValue('deliveryState', null);
									}}

									country={country}
								/>
							)}
						/>
					</Column>
				</Row>
			)}
			viewContent={(
				<Field name="specialInstructions">
					{({ form }: FieldProps<FormValues>) => getCardFields(getDeliveryInfoFields(form.values, t, details))}
				</Field>
			)}
		/>
	);
};
